// WHAT EACH CONTRACTOR HAS SPENT, AND WHAT IT COST, ACROSS EVERY SYSTEM.
//
// Owner, 2026-10-07: "i need a company admin interface in coordination, that
// colelcts all hours and builds qa full report that combines a contractor
// activties and hours and cost per their cost, from all billable systems on
// bidlight, like issues,shop drawings ...".
//
// THE ONE THING THIS PAGE MUST NOT DO is show a total that looks like an
// invoice when part of it is unpriced. Two systems feed it and neither always
// carries a rate: issues store a cost computed at submission, shop drawings
// store hours and a rate. (Sheet imports were a third until 2026-10-10: their
// hours are the delivering issue's, already on its ledger.) So an hour can be
// priced, derivable, or unpriceable - and the third kind is
// counted in the hours and left out of the money, with the page SAYING SO on
// the row and in the total rather than in a footnote nobody reads.
//
// A cost column that quietly treated unpriced work as free would understate
// an invoice by exactly the amount nobody can see.
import PeoplePicker from "components/PeoplePicker/PeoplePicker";
import { loadRoster } from "components/PeoplePicker/PeoplePicker";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Checkbox, DatePicker, Dropdown, Input, InputNumber, Modal, Select, Switch, Table, Tabs, Tag, Tooltip, message } from "antd";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import { bulkIgnoreItems, bulkSetItemOverrides, exportBillableHours, getBillableRows, reportQuery, setContractorNoCost, setItemsNoCost } from "api/billableHours";
import SendReportModal from "./SendReportModal";
import { withSorting, useRememberedSort } from "helpers/tableSort";
import ShopDrawingFilterBar, { useShopDrawingFilters } from "pages/ShopDrawings/ShopDrawingFilterBar";
import { getShopDrawingFacets, shopDrawingFilterQuery } from "api/tracking/shopDrawingFilterApi";
import ContractorRows, { NO_COST_HELP, isRateEditable, itemKey } from "./ContractorRows";
import RateOverrides from "./RateOverrides";
import IgnoredLines from "./IgnoredLines";
import ReportHistory from "./ReportHistory";
import { confirmExportNotDuplicate } from "./exportPrecheck";
import { WhoSeesRate } from "./rateAudience";
import { usePreference } from "helpers/userPrefs";
import { savedFilterQuery, presetsForProject, findPreset } from "./savedFilters";
import { searchReport } from "./costSearch";
import { usePartnerTrades } from "components/Partners/partnerTrades";
import { partnerOptions, decorate, PartnerTeamSelect } from "./partnerFilters";
import { PartnerWithTrades } from "components/Partners/PartnerTradeLabel";
import ActivityFilterSelect from "components/Coordination/ActivityFilterSelect";
import { matchOption } from "components/SearchSelect/searchSelect";

const T = { muted: "#667085", line: "#EFEFEF", amber: "#b54708", green: "#17803d" };
const SYSTEM_LABEL = {
  issues: "Issues",
  shopDrawings: "Shop drawings",
};
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const hrs = (n) => `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} h`;
const r2 = (n) => Math.round(n * 100) / 100;

// WHAT A SELECTION ADDS UP TO, from the report itself: items (the unit a rate
// is saved on), the ledger lines under them, their hours and today's cost.
export function selectionSummary(contractors, selected) {
  const out = { items: 0, issues: 0, shopDrawings: 0, lines: 0, hours: 0, cost: 0 };
  const items = new Set();
  for (const c of contractors || []) {
    for (const r of c.rows || []) {
      const k = itemKey(r);
      if (!selected.has(k)) continue;
      out.lines += 1;
      out.hours += Number(r.hours) || 0;
      out.cost += Number(r.cost) || 0;
      if (!items.has(k)) { items.add(k); out[r.system] += 1; }
    }
  }
  out.items = items.size;
  out.hours = r2(out.hours);
  out.cost = r2(out.cost);
  return out;
}

// Every line in the current (filtered) results that can take a rate, as
// selection entries. `kind` is "all" | "issues" | "shopDrawings".
export function selectableItems(contractors, kind = "all") {
  const out = new Map();
  for (const c of contractors || []) {
    for (const r of c.rows || []) {
      if (!isRateEditable(r) || (kind !== "all" && r.system !== kind)) continue;
      out.set(itemKey(r), {
        system: r.system, ref: r.ref, contractorCompanyId: r.companyId || "", contractorName: c.companyName || "",
      });
    }
  }
  return out;
}

// THE CONTRACTOR'S "NO COST" BOX (owner, 2026-10-12: "add an option to void
// hours for a contractor as a checkbox"). Per project on the server; here it
// covers the projects this contractor has lines on in the current results,
// and says which. Ticked = every one of them, a dash = some.
export function ContractorNoCostBox({ contractor, projects = [], onChanged }) {
  const [busy, setBusy] = useState(false);
  const pids = contractor.projectIds || [];
  const nc = contractor.noCost || {};
  if (!pids.length) return null;
  const titleOf = (id) => (projects.find((p) => p._id === id) || {}).title || id;
  const names = pids.map(titleOf).join(", ");
  const toggle = (e) => {
    const next = e.target.checked;
    setBusy(true);
    setContractorNoCost({
      contractorCompanyId: contractor.companyId || "", contractorName: contractor.companyName || "", projectIds: pids, noCost: next,
    })
      .then(() => { message.success(next ? `${contractor.companyName || "Contractor"}: no cost on ${pids.length} project${pids.length === 1 ? "" : "s"}` : "Priced again"); if (onChanged) onChanged(); })
      .catch((err) => message.error((err && (err.err || err.message)) || "Could not change it"))
      .finally(() => setBusy(false));
  };
  const who = (nc.projects || []).map((m) => `${titleOf(m.projectId)}: set by ${m.by || "someone"}${m.at ? ` on ${new Date(m.at).toLocaleDateString()}` : ""}`).join("; ");
  return (
    <Tooltip title={`${NO_COST_HELP} Covers this contractor's hours on ${names}.${who ? ` ${who}.` : ""}`}>
      {/* stopPropagation: the row also expands on click. */}
      <span onClick={(e) => e.stopPropagation()} style={{ marginLeft: 10, fontSize: 12, whiteSpace: "nowrap" }}>
        <Checkbox checked={!!nc.all} indeterminate={!nc.all && !!nc.some} disabled={busy} onChange={toggle} aria-label={`No cost for ${contractor.companyName || "this contractor"}`}>
          No cost
        </Checkbox>
      </span>
    </Tooltip>
  );
}

// The statuses each kind can be filtered on (the lists' own words).
const ISSUE_STATUS_OPTIONS = [
  { value: "open", label: "Open" }, { value: "pending", label: "Pending" }, { value: "under_review", label: "Under review" },
  { value: "resolved", label: "Resolved" }, { value: "abandoned", label: "Abandoned" },
];
const DRAWING_STATUS_OPTIONS = [
  { value: "requested", label: "Requested" }, { value: "submitted", label: "Delivered" }, { value: "under_review", label: "Waiting for approval" },
  { value: "approved", label: "Approved" }, { value: "rejected", label: "Sent back" },
];

export default function ContractorHoursPage() {
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const [projectId, setProjectId] = useState("");
  const [range, setRange] = useState(null);
  const [prices, setPrices] = useState(true);
  const [exporting, setExporting] = useState("");
  const [sendArgs, setSendArgs] = useState(null); // { query, items, prices } while the send window is open
  const [contractorId, setContractorId] = useState("");
  // The contractor picker's list, kept from the last UNFILTERED load: once
  // the report is narrowed to one contractor it only carries that one.
  const [reported, setReported] = useState([]);
  // THE GROUP SELECTION: item key -> what the bulk save needs. One selection
  // across every contractor in the results; it is cleared whenever the
  // results reload, so it can never hold a line the page is not showing.
  const [selected, setSelected] = useState(() => new Map());
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupRate, setGroupRate] = useState(null);
  const [groupAudience, setGroupAudience] = useState("owner");
  const [groupSaving, setGroupSaving] = useState(false);
  // LEAVE THE SELECTION OUT OF THE REPORT (owner, 2026-10-11).
  const [ignoreOpen, setIgnoreOpen] = useState(false);
  const [ignoreReason, setIgnoreReason] = useState("");
  const [ignoring, setIgnoring] = useState(false);
  // NO COST FOR THE SELECTION (owner, 2026-10-12: "like bulk override price").
  const [noCostBusy, setNoCostBusy] = useState("");
  // SAVED FILTERS FROM THE ISSUES MANAGER AND SHOP DRAWINGS (owner,
  // 2026-10-15: "save it so it can be pulled from cost contractor and also
  // applied in conjunction with issues saved filters"). The reader's own
  // saved filters, by name; both may be on, each narrowing its own kind.
  const [issuePresets] = usePreference("issues.savedFilters", []);
  const [issueFilterName, setIssueFilterName] = useState("");
  // EVERY SHOP DRAWING PROPERTY AS A FILTER (owner, 2026-10-23: "do the same
  // filter on the contractor cost table" - as on Document Control). The
  // register's own bar (pages/ShopDrawings/ShopDrawingFilterBar), its saved
  // filters among them, sent as ?shopDrawingFilter= and answered by the
  // register's own query (routes/billableHours savedFilterItems). It narrows
  // the shop drawing lines; issue lines follow the issue filters. One project
  // at a time - a drawing belongs to one.
  const [sdFacets, setSdFacets] = useState(null);
  const [sdOpen, setSdOpen] = useState(false);
  const sf = useShopDrawingFilters(sdOpen && sdFacets ? (projectId || "") : "");
  useEffect(() => {
    let alive = true;
    setSdFacets(null);
    sf.setFilters({});
    if (!projectId) return undefined;
    // No facets = the register is not this person's to read; the bar stays away.
    getShopDrawingFacets(projectId).then((f) => { if (alive) setSdFacets(f); }).catch(() => {});
    return () => { alive = false; };
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Typing waits a moment, like the register's own search.
  const sdNow = projectId && sdFacets && sf.anyFilter
    ? shopDrawingFilterQuery({ ...sf.filters, tzOffset: new Date().getTimezoneOffset() }) : "";
  const [sdQuery, setSdQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSdQuery(sdNow), sf.filters.search ? 350 : 0);
    return () => clearTimeout(t);
  }, [sdNow]); // eslint-disable-line react-hooks/exhaustive-deps
  // The name the report's notes and emails print: the saved filter it is,
  // when it is one unchanged, else what it is.
  const sdName = useMemo(() => {
    const same = (sf.savedDrawingFilters || []).find((f) => JSON.stringify(f.filters || {}) === JSON.stringify(sf.filters));
    return same ? same.name : "shop drawing filters";
  }, [sf.savedDrawingFilters, sf.filters]);
  // THE ACTIVITY, at any level, with everything below it (owner, 2026-10-15),
  // on the issues and the drawings filed under it. Needs a project: the tree
  // is that project's schedule. Cleared when the project changes.
  const [activityPick, setActivityPick] = useState("");
  // RECORDED OR ESTIMATED, AND OPEN ONLY (owner, 2026-10-16: "filter open
  // issues, and project the cost per estimated hours"). Both ride with every
  // read and every export, like the saved filters.
  const [basis, setBasisRaw] = useState("recorded");
  const [openOnly, setOpenOnly] = useState(false);
  // Estimated opens on what is still to do (owner, 2026-10-16: "issues that
  // are estimated and not yet resolved, and shop drawings ... not yet
  // delivered"); the switch stays there to widen it.
  const setBasis = (v) => { setBasisRaw(v); setOpenOnly(v === "estimated"); };
  // THREE TABS (owner, 2026-10-22): recorded hours, estimated hours, and the
  // log of reports already exported. The first two are the basis switch that
  // used to be a Segmented control inside the toolbar - the same state, moved
  // to where the owner asked for it, so nothing about the report changed.
  const [tab, setTab] = useState("recorded");
  const onTab = (k) => { setTab(k); if (k === "recorded" || k === "estimated") setBasis(k); };
  // TRADE, STATUS, TAG - on issues and drawings alike (owner, 2026-10-16).
  const [trade, setTrade] = useState("");
  const [issueStatus, setIssueStatus] = useState([]);
  const [drawingStatus, setDrawingStatus] = useState([]);
  const [tag, setTag] = useState("");
  // ASSIGNED TO, one person (owner, 2026-10-16): { value, name }.
  const [assignee, setAssignee] = useState(null);
  useEffect(() => { setAssignee(null); }, [projectId]);
  const [tagDraft, setTagDraft] = useState("");
  const [tradeOptions, setTradeOptions] = useState([]);
  // THE ROSTER ITSELF, kept: both the trade list and the two partner
  // dropdowns are built from it, and it is one cached fetch per project.
  const [roster, setRoster] = useState([]);
  const [partnerTeam, setPartnerTeam] = useState("");
  // SEARCH ACROSS EVERY COLUMN (owner, 2026-10-22). It narrows what the
  // TABLE shows and deliberately does not touch the totals above it - those
  // are the report's, for the filters that built it. See the note beside the
  // box: a search that quietly re-totalled the page would put a different
  // number under the same heading.
  const [search, setSearch] = useState("");
  useEffect(() => {
    let alive = true;
    if (!projectId) { setTradeOptions([]); setRoster([]); return undefined; }
    loadRoster(projectId).then((r) => {
      if (!alive) return;
      setRoster(r || []);
      const seen = new Map();
      (r || []).filter((g) => g.role !== "owner").forEach((g) => (g.trades || []).forEach((t, i) => {
        if (!seen.has(t)) seen.set(t, (g.tradeLabels && g.tradeLabels[i]) || t);
      }));
      setTradeOptions([...seen].map(([value, label]) => ({ value, label: label === value ? value : `${value} (${label})` })));
    });
    return () => { alive = false; };
  }, [projectId]);
  // A partner team is a project's roster; clearing the project clears it, or
  // the report would stay narrowed by a company nobody can see in the box.
  useEffect(() => { setPartnerTeam(""); }, [projectId]);
  // A PRESET NAMED ON ANOTHER PROJECT IS CLEARED WITH THE PROJECT. Leaving
  // the name in the box after the project moves is how the page ends up
  // saying "Open electrical" while filtering by nothing at all - findPreset
  // returns null for another project's filter, so the name would be a label
  // on an absent filter.
  useEffect(() => {
    if (issueFilterName && !findPreset(issuePresets, issueFilterName, projectId)) setIssueFilterName("");
  }, [projectId, issuePresets]); // eslint-disable-line react-hooks/exhaustive-deps
  const estimated = basis === "estimated";
  const partnerTrades = usePartnerTrades(projectId || null);
  // THE CONTRACTOR BOX: every partner on the project plus anyone in the
  // report who is no longer on it, each with its trades, and a note on the
  // ones with nothing in the report as it stands.
  const contractorOptions = useMemo(
    () => decorate(partnerOptions(roster, reported), partnerTrades),
    [roster, reported, partnerTrades],
  );
  // THE PARTNER TEAM BOX: the roster only - "whose people hold this" is a
  // question about the project, not about who happens to have billed. No
  // "no hours" note here, because hours are not what it filters on.
  const teamOptions = useMemo(
    () => decorate(partnerOptions(roster, [], { includeOwner: true }), partnerTrades, { markEmpty: false }),
    [roster, partnerTrades],
  );
  useEffect(() => { setActivityPick(""); }, [projectId]);
  const savedParams = useMemo(() => {
    const out = {};
    const ip = findPreset(issuePresets, issueFilterName, projectId);
    if (ip) { out.issueFilter = savedFilterQuery(ip, "issues"); out.issueFilterName = ip.name; }
    if (sdQuery && projectId) { out.shopDrawingFilter = sdQuery; out.shopDrawingFilterName = sdName; }
    if (activityPick && projectId) out.ganttItemId = activityPick;
    if (basis === "estimated") out.basis = "estimated";
    if (openOnly) out.openOnly = true;
    if (trade) out.trade = trade;
    if (issueStatus.length) out.issueStatus = issueStatus;
    if (drawingStatus.length) out.drawingStatus = drawingStatus;
    if (tag) out.tag = tag;
    if (assignee) { out.assignee = assignee.value; out.assigneeName = assignee.name; }
    if (partnerTeam && projectId) {
      out.partnerTeam = partnerTeam;
      const g = (roster || []).find((x) => String(x.companyId) === String(partnerTeam));
      out.partnerTeamName = (g && g.companyName) || partnerTeam;
    }
    return out;
  }, [issuePresets, sdQuery, sdName, issueFilterName, activityPick, projectId, basis, openOnly, trade, issueStatus, drawingStatus, tag, assignee, partnerTeam, roster]);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    // The rows endpoint: the same totals as before plus every line item, from
    // one server pass, so the figures above and the rows below cannot disagree.
    setSelected(new Map());
    getBillableRows({
      projectId: projectId || undefined,
      contractorId: contractorId || undefined,
      since: range && range[0] ? range[0].startOf("day").toISOString() : undefined,
      until: range && range[1] ? range[1].endOf("day").toISOString() : undefined,
      ...savedParams,
    })
      .then((data) => {
        // A refusal arrives as a resolved { err } (api get), not a throw: it is
        // the error, never the report - reading totals off it blanked the page.
        if (!data || data.err || !data.totals) {
          setState({ loading: false, data: null, error: (data && data.err) || "Could not build the report" });
          return;
        }
        setState({ loading: false, data, error: "" });
        // EVERY PARTNER, NOT ONLY THE ONES WITH LINES (owner, 2026-10-22:
        // "show all partners"). Built in partnerFilters from the roster
        // unioned with this report's companies, so narrowing the dates no
        // longer makes partners vanish from the filter - and no longer
        // refreshed only while nothing is picked, which used to freeze the
        // list to whatever it held when a contractor was chosen.
        setReported((data.contractors || []).filter((c) => c.companyId));
      })
      .catch((e) => setState({ loading: false, data: null, error: (e && (e.err || e.message)) || "Could not build the report" }));
  }, [projectId, contractorId, range, savedParams]);

  useEffect(load, [load]);

  // THE DOCUMENT IS THE FULL REPORT FOR THE SAME FILTERS AS THE PAGE - every
  // line, not the rows that happen to be expanded. Hours only is built by the
  // server without rates or costs, so the file cannot carry a figure the
  // reader chose to leave out.
  const doExport = async (format) => {
    const args = {
      prices,
      projectId: projectId || undefined,
      contractorId: contractorId || undefined,
      since: range && range[0] ? range[0].startOf("day").toISOString() : undefined,
      until: range && range[1] ? range[1].endOf("day").toISOString() : undefined,
      ...savedParams,
    };
    // ALREADY EXPORTED? (owner, 2026-10-22) Asked BEFORE the document is built,
    // naming the items and when each went out. The check runs against the same
    // filters as the export, so it is about this document and not an
    // approximation of it. Abandoning here costs nothing; finding out after
    // the invoice went out costs the account's credibility.
    setExporting(format);
    try {
      const go = await confirmExportNotDuplicate(args, { prices });
      if (!go) { setExporting(""); return; }
    } catch (e) {
      // confirmExportNotDuplicate already degrades to "export anyway" on a
      // failed check; reaching here would be the dialog itself failing.
      setExporting("");
      message.error("Could not open the export check");
      return;
    }
    exportBillableHours(format, args)
      .then(({ lines, filename }) => message.success(`${filename}: ${lines} line${lines === 1 ? "" : "s"}`))
      .catch((e) => message.error((e && e.message) || "Could not export the report"))
      .finally(() => setExporting(""));
  };

  // SEND THE REPORT (owner, 2026-10-21): the same filters, or only the
  // ticked lines when some are ticked; the window previews who receives it.
  const openSend = () => setSendArgs({
    query: reportQuery({
      projectId: projectId || undefined,
      contractorId: contractorId || undefined,
      since: range && range[0] ? range[0].startOf("day").toISOString() : undefined,
      until: range && range[1] ? range[1].endOf("day").toISOString() : undefined,
      ...savedParams,
    }),
    items: selected.size ? [...selected.keys()] : null,
    prices,
  });

  const d = state.data;
  const systems = (d && d.systems) || ["issues", "shopDrawings"];
  const canEdit = !!(d && d.canEdit);
  // No-cost and ignored lines are contractorRatesEdit on the server
  // (routes/billableHours, 2026-10-14); an older answer without the per-key
  // flags falls back to canEdit.
  const canEditRates = !!(d && (d.canEditRates !== undefined ? d.canEditRates : d.canEdit));
  // SAVED FILTERS BELONG TO A PROJECT (owner, 2026-10-22). Both lists are one
  // per-user preference, so without this every project's filters were on the
  // menu for every project.
  const projectNames = useMemo(() => Object.fromEntries(((d && d.projects) || []).map((p) => [String(p._id), p.title || ""])), [d]);
  const issuePresetOptions = useMemo(
    () => presetsForProject(issuePresets, projectId, { names: projectNames }).map((f) => ({ value: f.name, label: f.label })),
    [issuePresets, projectId, projectNames],
  );
  const allContractors = (d && d.contractors) || [];
  const found = searchReport(allContractors, search);
  const contractors = found.contractors;
  const sel = selectionSummary(contractors, selected);
  const available = {
    all: selectableItems(contractors, "all").size,
    issues: selectableItems(contractors, "issues").size,
    shopDrawings: selectableItems(contractors, "shopDrawings").size,
  };

  const onSelectRows = (rows, checked, contractor) => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const r of rows) {
        if (!isRateEditable(r)) continue;
        if (checked) {
          next.set(itemKey(r), {
            system: r.system, ref: r.ref, contractorCompanyId: r.companyId || "",
            contractorName: (contractor && contractor.companyName) || "",
          });
        } else {
          next.delete(itemKey(r));
        }
      }
      return next;
    });
  };

  const ignoreSelected = () => {
    setIgnoring(true);
    bulkIgnoreItems({ items: [...selected.values()], reason: ignoreReason })
      .then((r) => {
        message.success(`${r.ignored} item${r.ignored === 1 ? "" : "s"} left out of the report${r.alreadyIgnored ? ` (${r.alreadyIgnored} already were)` : ""}`);
        setIgnoreOpen(false);
        load();
      })
      .catch((e) => message.error((e && (e.err || e.message)) || "Could not ignore those lines"))
      .finally(() => setIgnoring(false));
  };

  const noCostSelected = (noCost) => {
    setNoCostBusy(noCost ? "on" : "off");
    setItemsNoCost([...selected.values()], noCost)
      .then((r) => {
        message.success(noCost
          ? `${r.changed} item${r.changed === 1 ? "" : "s"} marked no cost${r.unchanged ? ` (${r.unchanged} already were)` : ""}`
          : `${r.changed} item${r.changed === 1 ? "" : "s"} priced again${r.unchanged ? ` (${r.unchanged} were not marked no cost)` : ""}`);
        load();
      })
      .catch((e) => message.error((e && (e.err || e.message)) || "Could not change them"))
      .finally(() => setNoCostBusy(""));
  };

  const saveGroup = () => {
    setGroupSaving(true);
    bulkSetItemOverrides({ items: [...selected.values()], rate: groupRate, appliesTo: groupAudience })
      .then((r) => {
        message.success(`$${groupRate}/h saved on ${r.saved} item${r.saved === 1 ? "" : "s"} (${r.issues} issue${r.issues === 1 ? "" : "s"}, ${r.shopDrawings} shop drawing${r.shopDrawings === 1 ? "" : "s"})`);
        setGroupOpen(false);
        load();
      })
      // The server's own words: a refused batch saved nothing and says why.
      .catch((e) => message.error((e && (e.err || e.message)) || "Could not save the rate"))
      .finally(() => setGroupSaving(false));
  };

  // EVERY HEADER SORTS (owner, 2026-10-23: "do the same sort on the contractor
  // cost table"), on what the cell shows, remembered per person
  // (helpers/tableSort). A system with no hours is an empty cell - last both ways.
  const [contractorSort, onContractorSort] = useRememberedSort("contractorCost.sort");
  const columns = useMemo(() => withSorting([
    {
      title: "Contractor", dataIndex: "companyName", key: "c", fixed: "left",
      render: (v, r) => (
        <span>
          {/* Its trades beneath when a project is picked (owner, 2026-10-16) -
              across projects a partner's trades differ, so none are claimed. */}
          {projectId && v
            ? <PartnerWithTrades name={v} trades={partnerTrades.tradesOf(r.companyId)} />
            : (v || r.companyId || "(no company recorded)")}
          {r.unidentified && (
            <Tooltip title="These hours were recorded without a contractor company. They are in every total on this page and listed below so they can be traced, not left out.">
              <Tag color="red" style={{ marginLeft: 8 }}>unattributed</Tag>
            </Tooltip>
          )}
          {canEditRates && r.rows ? (
            <ContractorNoCostBox contractor={r} projects={(d && d.projects) || []} onChanged={load} />
          ) : null}
          {!r.costComplete && (
            <Tooltip title="Some of this contractor's hours have no rate anywhere, so they are counted in the hours and left out of the cost. The cost below is therefore a floor, not a total.">
              <Tag color="orange" style={{ marginLeft: 8 }}>cost incomplete</Tag>
            </Tooltip>
          )}
        </span>
      ),
    },
    ...systems.map((s) => ({
      title: SYSTEM_LABEL[s] || s,
      key: s,
      align: "right",
      render: (_, r) => {
        const v = r.systems && r.systems[s];
        // A system with nothing in it SAYS so: a bare dash beside two systems
        // with real numbers reads as a fault. (Prod 2026-10-09: five sheet
        // imports, none with hours.)
        if (!v || !v.rows) return <span style={{ color: "#999", fontSize: 12 }}>no hours recorded</span>;
        return (
          <span>
            <div>{hrs(v.hours)}</div>
            <div style={{ color: v.rowsWithoutRate ? T.amber : T.muted, fontSize: 12 }}>
              {money(v.cost)}{v.rowsWithoutRate ? ` + ${v.rowsWithoutRate} unpriced` : ""}
            </div>
          </span>
        );
      },
    })),
    { title: "Total hours", dataIndex: "hours", key: "h", align: "right", render: (v) => <b>{hrs(v)}</b> },
    {
      title: "Total cost", dataIndex: "cost", key: "m", align: "right",
      render: (v, r) => (
        <span>
          <b style={{ color: r.costComplete ? T.green : T.amber }}>{money(v)}{r.costComplete ? "" : " +"}</b>
          {r.noCostHours ? <div style={{ color: T.muted, fontSize: 12 }}>{hrs(r.noCostHours)} no cost</div> : null}
        </span>
      ),
    },
  ], {
    c: (r) => r.companyName || "",
    ...Object.fromEntries(systems.map((s) => [s, (r) => (r.systems && r.systems[s] && r.systems[s].rows ? Number(r.systems[s].hours) || 0 : null)])),
    h: (r) => (r.hours == null ? null : Number(r.hours)),
    m: (r) => (r.cost == null ? null : Number(r.cost)),
  }, contractorSort), [systems, canEdit, canEditRates, d, load, projectId, partnerTrades, contractorSort]);

  return (
    <div style={{ padding: 16 }}>
      <CoordinationBreadcrumbs current="Contractor hours & cost" />
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Contractor hours &amp; cost</h2>
        <div style={{ color: T.muted, fontSize: 13, marginTop: 2 }}>
          Every billable system on the account — issues and shop drawings — totalled per contractor. A line is priced at its own item rate if one is set, then the contractor&apos;s rate override, then the rate recorded when the work was submitted. A line or contractor marked No cost keeps its hours and costs $0.00.
        </div>
      </div>

      <Tabs
        activeKey={tab}
        onChange={onTab}
        items={[
          { key: "recorded", label: "Recorded hours" },
          { key: "estimated", label: "Estimated hours" },
          { key: "reports", label: "Reports" },
        ]}
      />

      {tab === "reports" ? <ReportHistory projectId={projectId} /> : (
      <>
      <Card size="small" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ color: T.muted, fontSize: 13 }}>Project</span>
          <Select filterOption={matchOption}
            style={{ minWidth: 260 }} size="small" allowClear showSearch optionFilterProp="label"
            placeholder={d && d.projects ? `All ${d.projects.length} projects` : "All projects"}
            value={projectId || undefined}
            onChange={(v) => setProjectId(v || "")}
            options={((d && d.projects) || []).map((p) => ({ value: p._id, label: p.title || p._id }))}
          />
          <span style={{ color: T.muted, fontSize: 13 }}>Contractor</span>
          {/* ONE CONTRACTOR OR SEVERAL (owner, 2026-10-23: "do the same
              contractor filter on the contractor cost export" - the recorded
              and the estimated hours tabs alike). Kept as "a,b": every call
              already sends contractorId through, and the server splits it. */}
          <Select filterOption={matchOption}
            style={{ minWidth: 220 }} size="small" allowClear showSearch optionFilterProp="search"
            mode="multiple" maxTagCount="responsive" aria-label="Contractor filter"
            optionLabelProp="text" popupMatchSelectWidth={false}
            placeholder="All contractors"
            value={contractorId ? contractorId.split(",") : []}
            onChange={(v) => setContractorId((v || []).join(","))}
            options={contractorOptions}
          />
          <PartnerTeamSelect
            value={partnerTeam} onChange={setPartnerTeam}
            options={teamOptions} disabled={!projectId}
          />
          <ActivityFilterSelect size="small" projectId={projectId || null} value={activityPick}
            onChange={(v) => setActivityPick(v || "")} placeholder="Activity" />
          <Tooltip title="Only the issues one of your saved Issues Manager filters lists. Save a filter there (Saved filters → + Save current) to offer it here.">
            <Select
              size="small" allowClear placeholder="Issue filter" style={{ minWidth: 160 }}
              aria-label="Saved issue filter"
              value={issueFilterName || undefined}
              onChange={(v) => setIssueFilterName(v || "")}
              options={issuePresetOptions}
              notFoundContent="No saved issue filters yet"
            />
          </Tooltip>
          {projectId && sdFacets ? (
            <Button size="small" type={sf.anyFilter ? "primary" : "default"}
                    onClick={() => setSdOpen((v) => (sf.anyFilter ? true : !v))}
                    aria-expanded={sdOpen || sf.anyFilter}>
              {`Filter by shop drawing${sf.anyFilter ? ` (${Object.keys(sf.filters).length} on)` : ""}`}
            </Button>
          ) : null}
          <Tooltip title={estimated
            ? "Issues not yet resolved (open, pending, under review) and shop drawings not yet delivered (requested, or sent back)."
            : "Only issues still open (open, pending, under review) and shop drawings not yet approved."}>
            <span style={{ color: T.muted, fontSize: 13 }}>
              <Switch size="small" checked={openOnly} onChange={setOpenOnly} aria-label="Open only" /> {estimated ? "Not yet resolved / delivered" : "Open only"}
            </span>
          </Tooltip>
          <Select filterOption={matchOption} size="small" allowClear showSearch placeholder={projectId ? "Trade" : "Trade (pick a project)"} style={{ minWidth: 150 }}
            aria-label="Trade" value={trade || undefined} onChange={(v) => setTrade(v || "")} options={tradeOptions} disabled={!projectId} />
          <Select size="small" allowClear mode="multiple" maxTagCount="responsive" placeholder="Issue status" style={{ minWidth: 150 }}
            aria-label="Issue status" value={issueStatus} onChange={setIssueStatus} options={ISSUE_STATUS_OPTIONS} />
          <Select size="small" allowClear mode="multiple" maxTagCount="responsive" placeholder="Drawing status" style={{ minWidth: 150 }}
            aria-label="Drawing status" value={drawingStatus} onChange={setDrawingStatus} options={DRAWING_STATUS_OPTIONS} />
          <PeoplePicker
            size="small" projectId={projectId || null} disabled={!projectId} style={{ minWidth: 200, width: 220 }}
            placeholder={projectId ? "Assigned to" : "Assigned to (pick a project)"} aria-label="Assigned to"
            personValue={(p) => (p.userId ? String(p.userId) : (p.email ? String(p.email).toLowerCase() : ""))}
            popupMatchSelectWidth={360}
            value={assignee ? assignee.value : undefined}
            onChange={(v, opt) => setAssignee(v ? { value: v, name: (opt && opt.label) || v } : null)}
          />
          <Tooltip title="An issue's tag or title tag; a shop drawing's package or system">
            <Input.Search size="small" allowClear placeholder="Tag" style={{ width: 130 }} aria-label="Tag"
              value={tagDraft} onChange={(e) => { setTagDraft(e.target.value); if (!e.target.value) setTag(""); }} onSearch={(v) => setTag(String(v || "").trim())} />
          </Tooltip>
          <span style={{ color: T.muted, fontSize: 13 }}>{estimated ? "Items created between" : "Hours recorded between"}</span>
          <DatePicker.RangePicker size="small" value={range} onChange={setRange} allowEmpty={[true, true]} />
          <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            <Tooltip title="Off: the file lists hours only - rates and costs are left out of it entirely.">
              <span style={{ color: T.muted, fontSize: 13 }}>
                <Switch size="small" checked={prices} onChange={setPrices} /> {prices ? "With prices" : "Hours only"}
              </span>
            </Tooltip>
            <Dropdown
              menu={{
                items: [
                  { key: "pdf", label: "PDF" },
                  { key: "xlsx", label: "Excel" },
                ],
                onClick: ({ key }) => doExport(key),
              }}
              disabled={!!exporting || !d}
            >
              <Button size="small" loading={!!exporting}>Export full report</Button>
            </Dropdown>
            <Tooltip title={projectId
              ? (selected.size ? "Send the ticked lines as a report: preview who receives it first" : "Send this report: preview who receives it first")
              : "Pick a project: a report is sent for one project"}>
              <Button size="small" type="primary" disabled={!projectId || !d} onClick={openSend}>
                {selected.size ? `Send report (${selected.size} ticked)` : "Send report"}
              </Button>
            </Tooltip>
          </span>
        </div>
        {projectId && sdFacets && (sdOpen || sf.anyFilter) ? (
          <div style={{ marginTop: 10 }}>
            <ShopDrawingFilterBar
              projectId={projectId} sf={sf} showDeliveredTo={!!sdFacets.ownerSide}
              facets={{ areas: sdFacets.areas || [], packages: sdFacets.packages || [], levels: sdFacets.levels || [], contractors: sdFacets.contractors || [] }}
              countText={sf.anyFilter ? "Shop drawing lines: only these drawings. Issue lines follow the issue filters." : "Any shop drawing property"}
            />
          </div>
        ) : null}
      </Card>

      {state.error ? <Alert type="error" showIcon message={state.error} /> : null}

      {/* The overrides, above the numbers they change - a reader should see
          the rule before the figure it produced. */}
      <RateOverrides
        contractors={contractors}
        projects={(d && d.projects) || []}
        onChanged={load}
        canEdit={canEdit}
        canAdd={!!(d && d.canAdd)} canEditRates={!!(d && d.canEditRates)} canDelete={!!(d && d.canDelete)}
        canViewRates={!!(d && d.canViewRates)}
      />

      {d && d.notes && d.notes.length ? (
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message="Read this before using the numbers"
          description={<div>{d.notes.map((n, i) => <div key={i}>{n}</div>)}</div>}
        />
      ) : null}

      {/* THE HEADLINE, AND ITS HONESTY. A total with unpriced hours in it is
          a floor and says so - the alternative is a figure somebody invoices
          from that is wrong by an amount the page knew and did not mention. */}
      {d ? (
        <Card size="small" style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 28, flexWrap: "wrap", alignItems: "baseline" }}>
            <div>
              <div style={{ color: T.muted, fontSize: 12 }}>{estimated ? "Estimated hours" : "Total hours"}</div>
              <div style={{ fontSize: 22, fontWeight: 600 }}>{hrs(d.totals.hours)}</div>
            </div>
            <div>
              <div style={{ color: T.muted, fontSize: 12 }}>{estimated ? "Projected cost" : "Total cost"}</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: d.totals.costComplete ? T.green : T.amber }}>
                {money(d.totals.cost)}{d.totals.costComplete ? "" : " +"}
              </div>
              {d.totals.recordedCost != null && d.totals.recordedCost !== d.totals.cost ? (
                <div style={{ color: T.muted, fontSize: 12 }}>recorded at submission {money(d.totals.recordedCost)}</div>
              ) : null}
            </div>
            <div>
              <div style={{ color: T.muted, fontSize: 12 }}>Contractors</div>
              <div style={{ fontSize: 22, fontWeight: 600 }}>{d.contractors.length}</div>
            </div>
            {!d.totals.costComplete ? (
              <div style={{ color: T.amber, fontSize: 13, maxWidth: 460 }}>
                {d.totals.rowsWithoutRate} entr{d.totals.rowsWithoutRate === 1 ? "y has" : "ies have"} hours with no rate on record,
                so they are counted in the hours and not in the cost. The cost above is a floor.
              </div>
            ) : null}
            {d.totals.rowsWithoutHours ? (
              <div style={{ color: T.muted, fontSize: 13 }}>
                {d.totals.rowsWithoutHours} entr{d.totals.rowsWithoutHours === 1 ? "y was" : "ies were"} submitted without hours — not zero hours, simply not entered.
              </div>
            ) : null}
          </div>
        </Card>
      ) : null}

      {/* SELECTED LINES (owner, 2026-10-11): one rate for all of them, or
          leave them out of the report. Filter the results above, select here
          or with the boxes under each contractor. */}
      {canEdit && d ? (
        <Card size="small" style={{ marginBottom: 12, position: "sticky", top: "var(--app-bars-h, 0px)", zIndex: 3 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <b>Selected lines</b>
            <Dropdown
              disabled={!available.all}
              menu={{
                items: [
                  { key: "all", label: `Every line in these results (${available.all})`, disabled: !available.all },
                  { key: "issues", label: `Issue lines only (${available.issues})`, disabled: !available.issues },
                  { key: "shopDrawings", label: `Shop drawing lines only (${available.shopDrawings})`, disabled: !available.shopDrawings },
                  { type: "divider" },
                  { key: "clear", label: "Clear the selection", disabled: !selected.size },
                ],
                onClick: ({ key }) => setSelected(key === "clear" ? new Map() : selectableItems(contractors, key)),
              }}
            >
              <Button size="small">Select…</Button>
            </Dropdown>
            {sel.items ? (
              <span style={{ fontSize: 13 }}>
                <b>{sel.items}</b> item{sel.items === 1 ? "" : "s"} selected
                <span style={{ color: T.muted }}>
                  {" "}({sel.issues} issue{sel.issues === 1 ? "" : "s"}, {sel.shopDrawings} shop drawing{sel.shopDrawings === 1 ? "" : "s"}) · {hrs(sel.hours)} · {money(sel.cost)} now
                </span>
              </span>
            ) : (
              <span style={{ color: T.muted, fontSize: 13 }}>
                Narrow the results with the filters above, then select lines here or with the boxes under each contractor.
              </span>
            )}
            <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <Tooltip title={NO_COST_HELP}>
                <Button size="small" disabled={!sel.items || !!noCostBusy} loading={noCostBusy === "on"} onClick={() => noCostSelected(true)}>
                  No cost
                </Button>
              </Tooltip>
              <Tooltip title="Price the selected lines again: removes their no-cost mark, so their item rate, contractor rate or recorded rate applies as before.">
                <Button size="small" disabled={!sel.items || !!noCostBusy} loading={noCostBusy === "off"} onClick={() => noCostSelected(false)}>
                  Restore cost
                </Button>
              </Tooltip>
              <Button
                size="small" danger disabled={!sel.items}
                onClick={() => { setIgnoreReason(""); setIgnoreOpen(true); }}
              >
                Ignore…
              </Button>
              <Button
                size="small" type="primary" disabled={!sel.items}
                onClick={() => { setGroupRate(null); setGroupAudience("owner"); setGroupOpen(true); }}
              >
                Set one rate…
              </Button>
            </span>
          </div>
        </Card>
      ) : null}

      <Modal
        open={ignoreOpen}
        title={`Leave ${sel.items} item${sel.items === 1 ? "" : "s"} out of the report`}
        okText="Ignore them"
        okButtonProps={{ danger: true, loading: ignoring }}
        onOk={ignoreSelected}
        onCancel={() => setIgnoreOpen(false)}
        destroyOnClose
      >
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ fontSize: 13 }}>
            {sel.issues} issue{sel.issues === 1 ? "" : "s"} and {sel.shopDrawings} shop drawing{sel.shopDrawings === 1 ? "" : "s"}
            {" "}({hrs(sel.hours)}, {money(sel.cost)}) will be left out of every total on this page and of the PDF and Excel exports.
          </div>
          <div style={{ color: T.muted, fontSize: 12.5 }}>
            Ignoring removes the lines, hours included. To keep the hours and charge nothing for them, use No cost instead.
            {" "}Nothing is deleted. They are listed under Ignored lines at the bottom, with who ignored them and why, and can be brought back from there.
            Their hours also leave the weekly and monthly report emails, the contractor's and executives' copies included. Issue exports are not affected.
          </div>
          <div>
            <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 4 }}>Why (optional, shown beside them)</div>
            <Input
              maxLength={500} placeholder="e.g. warranty work, not billable"
              value={ignoreReason} onChange={(e) => setIgnoreReason(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={groupOpen}
        title={`One rate for ${sel.items} item${sel.items === 1 ? "" : "s"}`}
        okText="Save the rate"
        okButtonProps={{ disabled: groupRate == null, loading: groupSaving }}
        onOk={saveGroup}
        onCancel={() => setGroupOpen(false)}
        destroyOnClose
      >
        <div style={{ display: "grid", gap: 14 }}>
          <div style={{ color: T.muted, fontSize: 13 }}>
            {sel.issues} issue{sel.issues === 1 ? "" : "s"} and {sel.shopDrawings} shop drawing{sel.shopDrawings === 1 ? "" : "s"}, {hrs(sel.hours)} in all.
            Each gets its own item rate, the same as setting it with the pencil on every line; an item that already has one is changed to this.
          </div>
          <div>
            <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 4 }}>Hourly rate</div>
            <InputNumber
              autoFocus style={{ width: 180 }} min={0} max={10000} prefix="$" addonAfter="/h"
              value={groupRate} onChange={setGroupRate}
            />
            {groupRate != null ? (
              <div style={{ color: T.muted, fontSize: 12, marginTop: 4 }}>
                These lines cost {money(sel.cost)} now; at ${groupRate}/h they cost {money(sel.hours * groupRate)}.
              </div>
            ) : null}
          </div>
          <div>
            <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 4 }}>Who sees this rate</div>
            <WhoSeesRate value={groupAudience} onChange={setGroupAudience} />
          </div>
        </div>
      </Modal>

      {/* SEARCH EVERY COLUMN (owner, 2026-10-22) - the contractor row and the
          lines beneath it, including the columns built at render time. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "4px 0 8px", flexWrap: "wrap" }}>
        <Input
          allowClear
          size="small"
          style={{ width: 280 }}
          placeholder="Search every column…"
          aria-label="Search the report"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {found.searching ? (
          <span data-testid="cost-search-note" style={{ color: T.muted, fontSize: 12.5 }}>
            {found.contractors.length === 0
              ? "Nothing on this report matches."
              : `${found.matchedLines} line${found.matchedLines === 1 ? "" : "s"} on ${found.contractors.length} contractor${found.contractors.length === 1 ? "" : "s"}`}
            {found.hiddenContractors > 0 ? ` · ${found.hiddenContractors} contractor${found.hiddenContractors === 1 ? "" : "s"} hidden` : ""}
            {" · the totals above are for the filters, not the search"}
          </span>
        ) : null}
      </div>
      <Table
        size="small" rowKey={(r) => r.companyId || r.companyName}
        loading={state.loading}
        dataSource={contractors}
        columns={columns}
        onChange={onContractorSort}
        pagination={false}
        scroll={{ x: true }}
        expandable={{
          // Every contractor opens onto its line items; the total under them
          // is the sum of the rows shown.
          rowExpandable: (r) => Array.isArray(r.rows) && r.rows.length > 0,
          expandedRowRender: (r) => (
            <ContractorRows contractor={r} canEdit={canEdit} onChanged={load} selected={selected} onSelectRows={onSelectRows} estimated={estimated} />
          ),
        }}
        locale={{ emptyText: state.loading ? "Totalling…" : (estimated ? "No issue or shop drawing in this selection carries estimated hours." : "No hours have been recorded on this account yet, in any system.") }}
      />

      {d ? <IgnoredLines ignored={d.ignored} canEdit={canEditRates} onChanged={load} /> : null}

      {d && d.scope ? (
        <div style={{ borderTop: `1px solid ${T.line}`, marginTop: 10, paddingTop: 8, color: T.muted, fontSize: 12 }}>
          {d.rowCount} entr{d.rowCount === 1 ? "y" : "ies"} across {d.scope.projectCount} of {d.scope.of} project{d.scope.of === 1 ? "" : "s"}.
        </div>
      ) : null}
      </>
      )}
      <SendReportModal open={!!sendArgs} onClose={() => setSendArgs(null)} {...(sendArgs || {})} />
    </div>
  );
}
